import React, { useState } from 'react';
import { getToken } from '../../functions/app_functions.js';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    CardElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import { trackEvent } from "../../utils/analytics";

const stripe_api_key = process.env.REACT_APP_STRIPE_API_KEY || process.env.STRIPE_API_KEY || null;
const stripePromise = stripe_api_key ? loadStripe(stripe_api_key) : Promise.resolve(null);

function CheckoutForm(props) {
    const stripe = useStripe();
    const elements = useElements();
    const [status, setStatus] = useState('');

    const handleSubmit = async (event) => {
        event.preventDefault();
        trackEvent("trip_purchase_payment_start", { trip_id: props.trip_id, user_id_action: props.user_id });

        if (!stripe || !elements) return;

        // Set laoding state
        props.setLoading();
        props.setLoading();
        setStatus('');

        try {
            // 1) Ask backend to create a PaymentIntent
            let response = await fetch(props.baseUrl + "/createpaymentintent", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${getToken()}`
                },
                body: JSON.stringify({
                    amount: props.amount,   // backend should still verify this server-side
                    currency: "usd",
                    user_id: props.user_id,
                    trip_id: props.trip_id,
                    trip_title: props.trip_title,
                    owner_name: props.owner_name,
                    owner_id: props.owner_id,
                    items: props.items
                })
            });

            response = await response.json();
            const result = response.value;

            if (!result || !result.client_secret) {
                throw new Error(result?.error || "Unable to initialize payment");
            }

            // 2) Confirm the PaymentIntent with Stripe.js
            const card = elements.getElement(CardElement);

            const { error, paymentIntent } = await stripe.confirmCardPayment(
                result.client_secret,
                {
                    payment_method: {
                        card: card
                    }
                }
            );

            if (paymentIntent && paymentIntent.status === "succeeded") {
                setStatus("Payment successful!");
                trackEvent("trip_purchase_payment_success", {
                    trip_id: props.trip_id,
                    user_id_action: props.user_id
                });

                props.handlePurchase();
                props.unsetLoading();
                return;
            }
            else if (error) {
                setStatus(`Payment failed: ${error.message}`);
                props.setError("Payment failed", error.message);
                trackEvent("trip_purchase_payment_error", {
                    trip_id: props.trip_id,
                    user_id_action: props.user_id
                });
                props.unsetLoading();
                return;
            }

            setStatus(`Payment status: ${paymentIntent?.status || "unknown"}`);
            props.unsetLoading();

        } catch (err) {
            setStatus("Payment failed: Technical error");
            props.setError("Payment failed", err.message || "Technical error");
            trackEvent("trip_purchase_payment_error", {
                trip_id: props.trip_id,
                user_id_action: props.user_id
            });
            props.unsetLoading();
        }
    };


    return (
        <form onSubmit={handleSubmit}>
            <div className="trip__popup__element trip__popup__element--stripe-element">
                <CardElement
                    options={{
                        style: {
                            base: {
                                color: '#331317',
                                fontFamily: 'arial',
                                fontSize: '16px',
                                '::placeholder': {
                                    color: '#CECACB',
                                }
                            },
                            invalid: {
                                color: '#aa5375',
                                iconColor: '#aa5375',
                            },
                        },
                    }}
                />
            </div>

            {status &&
                <div className={"text-small" + (status.includes("Payment successful!") ? " text-color-primary" : " text-color-tertiary")}>
                    {status}
                </div>
            }

            <div className="trip__popup__element trip__popup__buttonbox">
                {status !== "Payment successful!" &&
                    <button className={"button button-primary"} type="submit" disabled={!stripe}>
                        Pay
                    </button>
                }
            </div>
        </form >
    );
}

export default function StripePayment(props) {
    if (!stripe_api_key) {
        return (
            <div className="text-small text-color-tertiary">
                Stripe is not configured. Set REACT_APP_STRIPE_API_KEY in your webapp environment.
            </div>
        );
    }

    return (
        <Elements stripe={stripePromise}>
            <CheckoutForm {...props} />
        </Elements>
    );
}
